'use client';

import ScrollFade from '@/components/ui/ScrollFade/ScrollFade';
import { createStagingInfo, odds, totalStock } from '@/features/lottery/utils';
import { getLocalizedText } from '@/lib/l18n/function';
import utilsStyles from '@/styles/modules/utils.module.scss';
import type { LotteryPrizeStagingHandler } from '@monorepo/common';
import { type MutableRefObject, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { LotteryCustomOption, LotteryDrawHistoryLineup, LotteryPrize } from '../../types';
import LotteryGoodsList from '../LotteryGoodsList/LotteryGoodsList';
import LotteryPrizeStaging from '../LotteryPrizeStaging/LotteryPrizeStaging';
import styles from './LotteryPrizeList.module.scss';

function LotteryPrizeList({
  lotteryStatus,
  prizes,
  lotteryDrawHistory,
  backgroundColor,
  hideOdds = false,
  isHideStaging = false,
  customOptions,
}: {
  lotteryStatus: string;
  prizes: Array<LotteryPrize>;
  lotteryDrawHistory?: Array<LotteryDrawHistoryLineup>;
  backgroundColor?: string;
  hideOdds?: boolean;
  isHideStaging?: boolean;
  customOptions?: LotteryCustomOption;
}) {
  const stagingRef = useRef<LotteryPrizeStagingHandler>();

  const fadeinCallback = (playId: number) => {
    // stagingRankのアニメーション発火
    stagingRef.current?.play(playId);
  };

  const { t, i18n } = useTranslation();

  const showRankAnimation = () => {
    if (isHideStaging) return false;
    return customOptions?.hidePrizeLabel !== '1';
  };

  const generateStyle = (prize: LotteryPrize) => {
    if (!prize.stagingKey.includes('A')) return {};
    return {
      background: backgroundColor,
    };
  };

  return (
    <>
      <ul className={styles.componentStyle}>
        {prizes.map((prize) => (
          <ScrollFade tag={'li'} key={`prize_${prize.id}`} fadeinCallback={() => fadeinCallback(prize.id)}>
            <div className={styles.itemCard} style={generateStyle(prize)}>
              {/* 在庫・確率 */}
              <div className={styles.statusWrapper}>
                {totalStock(prize.goods) === 0 && <p className={styles.outStock}>品切れ</p>}
                {!hideOdds && (
                  <p>
                    {t('odds')}:&nbsp;
                    {odds(prize.id, prize.odds, lotteryDrawHistory)}%
                  </p>
                )}
              </div>
              {/* ランクアニメーション */}
              {showRankAnimation() && lotteryPrizeStaging(prize.id, prize.stagingKey, stagingRef)}

              {/* 景品ラベル名 */}
              <div className={`${styles.itemName} ${utilsStyles.textLarge} ${utilsStyles.textBold}`}>
                {getLocalizedText(i18n.resolvedLanguage, { ja: prize.name, en: prize.categoryName_en })}
              </div>
              {/* 景品ラベル詳細 */}
              <div className={`${styles.itemDescription} ${utilsStyles.textSmall}`}>
                {getLocalizedText(i18n.resolvedLanguage, { ja: prize.description, en: prize.description_en })}
              </div>
              {/* 景品一覧 */}
              <LotteryGoodsList
                goodsList={prize.goods}
                linupId={prize.id}
                lotteryDrawHistory={lotteryDrawHistory}
                liKeyPrefix={'prize_goods'}
                lotteryStatus={lotteryStatus}
                customOptions={customOptions}
              />
            </div>
          </ScrollFade>
        ))}
      </ul>
    </>
  );
}

function lotteryPrizeStaging(
  prizeId: number,
  stagingKey: string,
  ref: MutableRefObject<LotteryPrizeStagingHandler | undefined>,
) {
  const stagingInfo = createStagingInfo(stagingKey);
  return (
    <div className={styles.itemStaging}>
      <LotteryPrizeStaging
        id={prizeId}
        stagingRank={stagingInfo.stagingRank}
        stagingText={stagingInfo.stagingText}
        customRef={ref}
      />
    </div>
  );
}

export default LotteryPrizeList;
